(() => {
  if (customElements.get('veakoo-tallow-quiz')) return;
  const options = [
    { face: 'Face', body: 'Body', both: 'Face and body' },
    { light: 'Light cream', rich: 'Rich cream or balm', any: 'No texture preference' },
    { none: 'No added fragrance', any: 'No fragrance preference' },
    { simple: 'Simple base formula', enhanced: 'Added care ingredients', any: 'No formula preference' },
    { one: 'One product', three: 'Up to three products' }
  ];
  class TallowState {
    constructor() { this.reset(); }
    reset() { this.answers = [null, null, null, null, null]; this.index = 0; }
    select(value) {
      if (!options[this.index] || !Object.hasOwn(options[this.index], value)) return false;
      this.answers[this.index] = value;
      return true;
    }
    next() {
      if (!options[this.index] || !Object.hasOwn(options[this.index], this.answers[this.index])) return false;
      this.index += 1;
      return true;
    }
    back() { this.index = Math.max(0, this.index - 1); }
    complete() { return this.answers.every((value, i) => Object.hasOwn(options[i], value)); }
    match(candidates) {
      if (!this.complete()) return [];
      const [area, texture, fragrance, formula, count] = this.answers;
      const seen = new Set();
      return candidates.filter(product => {
        if (!product.id || product.available !== true || seen.has(product.id)) return false;
        if (!(product.area === area || product.area === 'both' && area !== 'both')) return false;
        if (texture !== 'any' && product.texture !== texture) return false;
        if (fragrance !== 'any' && product.fragrance !== fragrance) return false;
        if (formula !== 'any' && product.formula !== formula) return false;
        seen.add(product.id);
        return true;
      }).slice(0, count === 'one' ? 1 : 3);
    }
  }
  class TallowQuiz extends HTMLElement {
    static State = TallowState;
    constructor() { super(); this.state = new TallowState(); }
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const eventOptions = { signal: this.events.signal };
      this.status = this.querySelector('[data-status]');
      this.cards = Array.from(this.querySelectorAll('[data-product-id]'));
      this.candidates = this.cards.map(card => ({
        id: card.dataset.productId,
        available: card.dataset.available === 'true',
        area: card.dataset.area, texture: card.dataset.texture,
        fragrance: card.dataset.fragrance, formula: card.dataset.formula, card
      }));
      this.querySelector('[data-start]').hidden = false;
      this.querySelector('[data-fallback]').hidden = true;
      this.addEventListener('change', event => {
        if (!event.target.matches('input[type="radio"]')) return;
        const question = event.target.closest('[data-question]');
        if (Number(question?.dataset.question) !== this.state.index) return;
        this.state.select(event.target.value);
        this.status.textContent = '';
      }, eventOptions);
      this.addEventListener('submit', event => {
        const form = event.target.closest('[data-question]');
        if (!form) return;
        event.preventDefault();
        if (Number(form.dataset.question) !== this.state.index) return;
        const checked = form.querySelector('input:checked');
        if (!checked || !this.state.select(checked.value) || !this.state.next()) {
          this.status.textContent = 'Choose an answer to continue.';
          form.reportValidity();
          return;
        }
        if (this.state.index === options.length) this.renderResults();
        else this.show(`question-${this.state.index}`);
      }, eventOptions);
      this.addEventListener('click', event => {
        const control = event.target.closest('[data-action]');
        if (!control) return;
        switch (control.dataset.action) {
          case 'start': this.state.index = 0; this.show('question-0'); break;
          case 'back':
            if (this.state.index === 0) this.show('intro');
            else { this.state.back(); this.show(`question-${this.state.index}`); }
            break;
          case 'edit': this.state.back(); this.show(`question-${this.state.index}`); break;
          case 'restart': this.restart(); break;
        }
      }, eventOptions);
      this.restart(false);
    }
    disconnectedCallback() {
      this.events?.abort();
      this.events = null;
      this.cards = [];
      this.candidates = [];
      this.state.reset();
    }
    show(name, focus = true) {
      this.querySelectorAll('[data-screen]').forEach(screen => { screen.hidden = screen.dataset.screen !== name; });
      this.status.textContent = '';
      const position = name === 'intro' ? 1 : name === 'results' ? 7 : this.state.index + 2;
      this.querySelector('[data-progress]').textContent = `${position} / 7`;
      if (focus) this.querySelector(`[data-screen="${name}"] [data-focus]`)?.focus({ preventScroll: false });
    }
    renderResults() {
      if (!this.state.complete()) return;
      const matches = this.state.match(this.candidates);
      this.cards.forEach(card => { card.hidden = true; });
      const labels = {
        area: { face: 'Face use', body: 'Body use', both: 'Face and body use' },
        texture: { light: 'Light cream', rich: 'Rich cream or balm' },
        fragrance: { none: 'No added fragrance', added: 'Added fragrance' },
        formula: { simple: 'Simple base formula', enhanced: 'Added care ingredients' }
      };
      matches.forEach(product => {
        product.card.hidden = false;
        product.card.querySelector('[data-reason]').textContent = 'Configured attributes: ' + Object.keys(labels).map(key => labels[key][product[key]]).filter(Boolean).join(' · ');
      });
      this.querySelector('[data-products]').dataset.count = String(matches.length);
      this.querySelector('[data-summary]').textContent = this.state.answers.map((value, i) => options[i][value]).join(' · ');
      const count = this.querySelector('[data-match-count]');
      count.textContent = matches.length ? `${matches.length} ${matches.length === 1 ? 'option matches' : 'options match'} your selected preferences.` : 'No exact matches this time.';
      const empty = this.querySelector('[data-empty]');
      empty.hidden = matches.length > 0;
      if (!empty.hidden) {
        const message = !this.candidates.length ? 'No products have been configured for this guide yet.'
          : !this.candidates.some(product => product.available) ? 'The products configured for this guide are not currently available.'
            : 'No configured products match all of your selected preferences. Try changing your answers or browse the shop. We will not substitute unrelated products.';
        empty.querySelector('[data-empty-message]').textContent = message;
      }
      this.show('results');
    }
    restart(focus = true) {
      this.state.reset();
      this.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = false; });
      this.cards.forEach(card => { card.hidden = true; });
      this.show('intro', focus);
    }
  }
  customElements.define('veakoo-tallow-quiz', TallowQuiz);
})();
